import { ChevronRight, type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";

export interface NavMainItem {
  title: string;
  url: string;
  icon?: LucideIcon;
  target?: string;
  items?: {
    title: string;
    url: string;
    target?: string;
  }[];
}

export interface NavMainGroup {
  label?: string;
  items: NavMainItem[];
}

function NavRow({ item }: { item: NavMainItem }) {
  const location = useLocation();
  const pathname = location.pathname;
  const { state } = useSidebar();
  const hasChildren = Boolean(item.items?.length);
  const isActive =
    pathname === item.url ||
    (item.url !== "" && pathname.startsWith(item.url + "/"));
  const isChildActive = item.items?.some(
    (subItem) =>
      pathname === subItem.url || pathname.startsWith(subItem.url + "/"),
  );

  return (
    <>
      {hasChildren ? (
        // When collapsed: dropdown menu
        state === "collapsed" ? (
          <SidebarMenuItem key={item.title}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  tooltip={item.title}
                  isActive={isActive || isChildActive}
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  <ChevronRight className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                className="w-auto min-w-40"
              >
                {item.items?.map((subItem) => {
                  const isSubItemActive =
                    pathname === subItem.url ||
                    pathname.startsWith(subItem.url + "/");
                  return (
                    <DropdownMenuItem key={subItem.title} asChild>
                      <Link
                        to={subItem.url}
                        className={
                          isSubItemActive
                            ? "bg-accent text-accent-foreground"
                            : ""
                        }
                      >
                        {subItem.title}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        ) : (
          // When expanded: collapsible menu
          <Collapsible
            key={item.title}
            asChild
            defaultOpen={isActive || isChildActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton isActive={isActive || isChildActive}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.items?.map((subItem) => {
                    const isSubItemActive =
                      pathname === subItem.url ||
                      pathname.startsWith(subItem.url + "/");
                    return (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={isSubItemActive}
                        >
                          <Link to={subItem.url}>
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        )
      ) : (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton
            asChild
            tooltip={item.title}
            isActive={isActive}
          >
            <Link to={item.url}>
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
    </>
  );
}

export function NavMain({ groups }: { groups: NavMainGroup[] }) {
  return (
    <>
      {groups.map((group) => (
        <SidebarGroup key={group.label ?? "top"}>
          {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
          <SidebarMenu>
            {group.items.map((item) => (
              <NavRow key={item.title} item={item} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
}
